import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Droplets, Check, ShieldAlert, Thermometer, Package, Plus, Minus, AlertTriangle, Trash2, Stethoscope } from "lucide-react";
import api, { fmtDate } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { useFirma, OperatoreLocked } from "../context/FirmaContext";
import { EventiSanitariOspite } from "./EventiSanitariOspite";

const ymd = (d) => { const x = new Date(d); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`; };
const daysBetween = (a, b) => Math.round((new Date(b) - new Date(a)) / 86400000);
const WEEKDAYS = [[1, "Lun"], [2, "Mar"], [3, "Mer"], [4, "Gio"], [5, "Ven"], [6, "Sab"], [0, "Dom"]];
const dowLabel = (d) => (WEEKDAYS.find(([n]) => n === new Date(d).getDay()) || [0, ""])[1];
const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const curMese = () => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}`; };
const meseLabel = (mese) => { const [y, m] = mese.split("-").map(Number); return `${MESI[m - 1]} ${y}`; };
function useMese() {
  const [mese, setMese] = useState(curMese());
  const shiftMese = (n) => { const [y, m] = mese.split("-").map(Number); const d = new Date(y, m - 1 + n, 1); setMese(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`); };
  return [mese, shiftMese, setMese];
}
const MeseNav = ({ mese, shiftMese, setMese, testid }) => (
  <div className="flex items-center gap-1" data-testid={testid}>
    <Button variant="outline" size="sm" onClick={() => shiftMese(-1)} data-testid={`${testid}-prev`}><ChevronLeft size={16} /></Button>
    <button onClick={() => setMese(curMese())} className="min-w-[130px] text-center text-sm font-semibold px-2 py-1 rounded-md hover:bg-secondary transition-colors" title="Torna al mese corrente" data-testid={`${testid}-label`}>{meseLabel(mese)}</button>
    <Button variant="outline" size="sm" disabled={mese >= curMese()} onClick={() => shiftMese(1)} data-testid={`${testid}-next`}><ChevronRight size={16} /></Button>
  </div>
);


function useOperatori(structure_id) {
  const [operatori, setOperatori] = useState([]);
  useEffect(() => { api.get("/firma/operatori", { params: { structure_id } }).then((r) => setOperatori(r.data)).catch(() => setOperatori([])); }, [structure_id]);
  return operatori;
}
function useActiveOp() {
  const { opName, signerName, isPrivileged } = useFirma();
  const eff = opName || (isPrivileged ? signerName : "");
  const [op, setOp] = useState(eff);
  useEffect(() => { const v = opName || (isPrivileged ? signerName : ""); if (v) setOp(v); }, [opName, signerName, isPrivileged]);
  return [op, setOp];
}
const OpSelect = ({ op, setOp, operatori, testid }) => {
  const { opName, signerName, isPrivileged } = useFirma();
  if (opName) return <div className="w-56"><OperatoreLocked name={opName} testid={testid} /></div>;
  if (isPrivileged) return <div className="w-56"><OperatoreLocked name={signerName} testid={testid} /></div>;
  return (
    <div className="w-56"><Select value={op} onValueChange={setOp}><SelectTrigger data-testid={testid}><SelectValue placeholder="Operatore corrente" /></SelectTrigger>
      <SelectContent>{operatori.map((o) => <SelectItem key={o.id} value={`${o.nome} ${o.cognome}`}>{o.nome} {o.cognome}</SelectItem>)}</SelectContent></Select></div>
  );
};

// ============================ DOCCE ============================
export function DocceOspite({ ospite }) {
  const qc = useQueryClient();
  const operatori = useOperatori(ospite.structure_id);
  const [op, setOp] = useActiveOp();
  const today = ymd(new Date());
  const [mese, shiftMese, setMese] = useMese();
  const [giorni, setGiorni] = useState(null);
  const { data } = useQuery({ queryKey: ["docce", ospite.structure_id, mese], queryFn: async () => (await api.get("/docce/grid", { params: { structure_id: ospite.structure_id, mese } })).data });
  const row = (data?.rows || []).find((r) => r.ospite_id === ospite.id);
  const days = data?.days || []; const records = data?.records || {};
  const sched = giorni ?? row?.giorni_doccia ?? [];
  const toggle = async (d) => {
    const rec = records[`${ospite.id}_${d}`];
    const done = rec?.stato === "fatta";
    if (done) { if (!(rec?.operatore && rec.operatore === op)) { toast.error("Puoi annullare solo le tue spunte. Per quella di un altro operatore usa il pulsante ! (segnalazione)."); return; } }
    else if (!op) { toast.error("Seleziona prima l'operatore"); return; }
    try { await api.post("/docce/register", { ospite_id: ospite.id, structure_id: ospite.structure_id, data: d, stato: done ? "" : "fatta", operatore: op }); qc.invalidateQueries({ queryKey: ["docce"] }); }
    catch { toast.error("Errore"); }
  };
  const toggleDay = (n) => setGiorni((sched.includes(n) ? sched.filter((x) => x !== n) : [...sched, n]));
  const saveGiorni = async () => { try { await api.put(`/ospiti/${ospite.id}`, { giorni_doccia: sched }); qc.invalidateQueries({ queryKey: ["docce"] }); setGiorni(null); toast.success("Programma salvato"); } catch { toast.error("Errore"); } };
  const giorniFa = row?.ultima ? daysBetween(row.ultima, today) : null;

  return (
    <div className="space-y-4" data-testid="scheda-docce">
      <div className="gr-card p-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <span className="font-head font-bold flex items-center gap-2"><Droplets size={16} className="text-blue-600" /> Docce</span>
          <div className="flex items-center gap-2">
            <OpSelect op={op} setOp={setOp} operatori={operatori} testid="scheda-docce-op" />
            <MeseNav mese={mese} shiftMese={shiftMese} setMese={setMese} testid="scheda-docce-mese" />
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {days.map((d) => {
            const recD = records[`${ospite.id}_${d}`];
            const done = recD?.stato === "fatta";
            const altro = recD?.stato === "altro";
            const dow = new Date(d).getDay(); const scheduledDay = sched.includes(dow); const dueMissed = scheduledDay && !done && !altro && d <= today;
            return (
              <button key={d} onClick={() => toggle(d)} data-testid={`scheda-docce-cell-${d}`} title={altro ? `Altro: ${recD?.note || ""}` : undefined}
                className={`flex flex-col items-center rounded-lg border px-2 py-1.5 min-w-[52px] ${d === today ? "ring-2 ring-primary" : ""} ${done ? "bg-blue-600 text-white border-blue-600" : altro ? "bg-purple-600 text-white border-purple-600" : dueMissed ? "bg-amber-100 border-amber-400 text-amber-700" : "bg-secondary"}`}>
                <span className="text-[10px] uppercase leading-none">{dowLabel(d)}{d === today ? " · Oggi" : ""}</span>
                <span className="text-[10px] leading-none mt-0.5">{fmtDate(d).slice(0, 5)}</span>
                {done ? <Check size={16} /> : altro ? <span className="text-[9px] font-bold leading-none">ALT</span> : dueMissed ? <AlertTriangle size={14} /> : <Droplets size={14} className={scheduledDay ? "text-blue-400" : "opacity-20"} />}
              </button>
            );
          })}
        </div>
        <div className="mt-2 text-xs text-muted-foreground">Ultima doccia: {giorniFa === null ? "mai" : giorniFa === 0 ? "oggi" : `${giorniFa} giorni fa`}</div>
      </div>
      <div className="gr-card p-4">
        <div className="font-head font-bold text-sm mb-2">Programma settimanale</div>
        <div className="flex flex-wrap gap-2">
          {WEEKDAYS.map(([n, l]) => (
            <button key={n} onClick={() => toggleDay(n)} data-testid={`scheda-docce-day-${n}`}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${sched.includes(n) ? "bg-primary text-white border-primary" : "bg-white"}`}>{l}</button>
          ))}
          {giorni !== null && <Button size="sm" onClick={saveGiorni} data-testid="scheda-docce-save-giorni">Salva programma</Button>}
        </div>
      </div>
    </div>
  );
}

// ============================ INFETTIVE ============================
export function InfettiveOspite({ ospite }) {
  return <EventiSanitariOspite ospite={ospite} />;
}

// ============================ PANNOLONI ============================
const PANN_TIPI = [["pannoloni", "Pannoloni"], ["assorbenti", "Assorbenti"], ["mutandine", "Mutandine"]];
const PANN_PROVS = [["famiglia", "Famiglia"], ["usl", "USL"]];
const plabel = (arr, k) => (arr.find((x) => x[0] === k) || [k, k])[1];

export function PannoloniOspite({ ospite }) {
  const qc = useQueryClient();
  const operatori = useOperatori(ospite.structure_id);
  const [op, setOp] = useActiveOp();
  const [dlg, setDlg] = useState(null);
  const [form, setForm] = useState({});
  const { data: scorte = [] } = useQuery({ queryKey: ["pannoloni", ospite.structure_id], queryFn: async () => (await api.get("/pannoloni", { params: { structure_id: ospite.structure_id } })).data });
  const { data: movAll = [] } = useQuery({ queryKey: ["pannoloni-mov", ospite.structure_id], queryFn: async () => (await api.get("/pannoloni/movimenti", { params: { structure_id: ospite.structure_id } })).data });
  const rem = (tipo, prov) => scorte.find((s) => s.ospite_id === ospite.id && s.tipo === tipo && s.provenienza === prov)?.quantita ?? 0;
  const mov = movAll.filter((m) => m.ospite_id === ospite.id).slice(0, 20);
  const refresh = () => { qc.invalidateQueries({ queryKey: ["pannoloni"] }); qc.invalidateQueries({ queryKey: ["pannoloni-mov"] }); };
  const quickUsa = async (tipo, prov) => { if (!op) { toast.error("Seleziona prima l'operatore"); return; } try { await api.post("/pannoloni/usa", { ospite_id: ospite.id, ospite_nome: `${ospite.nome} ${ospite.cognome}`, structure_id: ospite.structure_id, tipo, provenienza: prov, quantita: 1, operatore_nome: op }); refresh(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } };
  const openDlg = (tipo, prov, mode) => { setForm({ quantita: mode === "usa" ? "1" : "", operatore_nome: op }); setDlg({ tipo, prov, mode }); };
  const submit = async () => { const qta = parseInt(form.quantita, 10); if (!qta || qta <= 0) { toast.error("Quantità non valida"); return; } if (!form.operatore_nome) { toast.error("Seleziona l'operatore"); return; } const url = dlg.mode === "carica" ? "/pannoloni/carica" : "/pannoloni/usa"; try { await api.post(url, { ospite_id: ospite.id, ospite_nome: `${ospite.nome} ${ospite.cognome}`, structure_id: ospite.structure_id, tipo: dlg.tipo, provenienza: dlg.prov, quantita: qta, operatore_nome: form.operatore_nome }); setDlg(null); refresh(); toast.success("Salvato"); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } };

  return (
    <div className="space-y-4" data-testid="scheda-pannoloni">
      <div className="gr-card p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <span className="font-head font-bold flex items-center gap-2"><Package size={16} /> Pannoloni e presidi</span>
          <OpSelect op={op} setOp={setOp} operatori={operatori} testid="scheda-pann-op" />
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          {PANN_TIPI.map(([tk, tl]) => {
            const tot = rem(tk, "famiglia") + rem(tk, "usl");
            return (
              <div key={tk} className="rounded-lg border p-2" data-testid={`scheda-pann-row-${tk}`}>
                <div className="flex items-center justify-between mb-1.5"><span className="text-xs font-bold">{tl}</span><span className="text-[11px] font-semibold text-muted-foreground">Tot. {tot}</span></div>
                <div className="grid grid-cols-2 gap-2">
                  {PANN_PROVS.map(([pk, pl]) => {
                    const rq = rem(tk, pk);
                    return (
                      <div key={pk} className="rounded-md border bg-secondary/20 p-1.5 text-center">
                        <div className="text-[10px] font-bold uppercase text-muted-foreground">{pl}</div>
                        <div className="text-xl font-extrabold tabnum text-primary" data-testid={`scheda-pann-rem-${tk}-${pk}`}>{rq}</div>
                        <div className="flex items-center justify-center gap-1 mt-1">
                          <button disabled={rq <= 0} onClick={() => quickUsa(tk, pk)} data-testid={`scheda-pann-usa1-${tk}-${pk}`} className="h-6 w-6 grid place-items-center rounded border hover:bg-white disabled:opacity-40"><Minus size={12} /></button>
                          <button onClick={() => openDlg(tk, pk, "usa")} className="h-6 px-1.5 grid place-items-center rounded border text-[10px] hover:bg-white">Usa</button>
                          <button onClick={() => openDlg(tk, pk, "carica")} data-testid={`scheda-pann-carica-${tk}-${pk}`} className="h-6 w-6 grid place-items-center rounded bg-primary text-white"><Plus size={12} /></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="gr-card p-4 overflow-x-auto">
        <div className="font-head font-bold text-sm mb-2">Ultimi movimenti</div>
        <table className="w-full text-sm">
          <thead><tr className="bg-secondary/40 text-xs uppercase tracking-wide text-muted-foreground"><th className="text-left p-2">Data</th><th className="text-left p-2">Articolo</th><th className="text-left p-2">Provenienza</th><th className="text-left p-2">Azione</th><th className="text-right p-2">Q.tà</th><th className="text-left p-2">Operatore</th></tr></thead>
          <tbody>
            {mov.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-muted-foreground">Nessun movimento</td></tr>}
            {mov.map((m) => (
              <tr key={m.id} className="border-t" data-testid={`scheda-pann-mov-${m.id}`}>
                <td className="p-2 whitespace-nowrap">{m.data ? fmtDate(m.data) : "—"}</td>
                <td className="p-2">{plabel(PANN_TIPI, m.tipo)}</td>
                <td className="p-2">{plabel(PANN_PROVS, m.provenienza)}</td>
                <td className="p-2"><span className={`text-xs font-bold ${m.azione === "carico" ? "text-emerald-600" : "text-red-600"}`}>{m.azione === "carico" ? "Carico" : "Consumo"}</span></td>
                <td className="p-2 text-right tabnum font-semibold">{m.azione === "carico" ? "+" : "−"}{m.quantita}</td>
                <td className="p-2 text-xs">{m.operatore || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!dlg} onOpenChange={(v) => !v && setDlg(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{dlg?.mode === "carica" ? "Carica scorta" : "Registra consumo"}</DialogTitle><DialogDescription>{dlg ? `${plabel(PANN_TIPI, dlg.tipo)} · ${plabel(PANN_PROVS, dlg.prov)}` : ""}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-semibold mb-1 block">Quantità</Label><Input type="number" min="1" value={form.quantita || ""} onChange={(e) => setForm((p) => ({ ...p, quantita: e.target.value }))} data-testid="scheda-pann-qta" autoFocus /></div>
            <div><Label className="text-xs font-semibold mb-1 block">Operatore *</Label>{op ? <OperatoreLocked name={op} testid="scheda-pann-dlg-op" /> : <Select value={form.operatore_nome || ""} onValueChange={(v) => setForm((p) => ({ ...p, operatore_nome: v }))}><SelectTrigger data-testid="scheda-pann-dlg-op"><SelectValue placeholder="Seleziona" /></SelectTrigger><SelectContent>{operatori.map((o) => <SelectItem key={o.id} value={`${o.nome} ${o.cognome}`}>{o.nome} {o.cognome}</SelectItem>)}</SelectContent></Select>}</div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDlg(null)}>Annulla</Button><Button onClick={submit} data-testid="scheda-pann-save">{dlg?.mode === "carica" ? "Carica" : "Registra"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================ CADUTE ============================
const CAD_ESITI = [["nessuna", "Nessuna conseguenza"], ["contusione", "Contusione / escoriazione"], ["ferita", "Ferita"], ["frattura", "Sospetta frattura"], ["trauma_cranico", "Trauma cranico"], ["ospedale", "Trasporto in ospedale"], ["altro", "Altro"]];
const cadEsitoLabel = (k) => (CAD_ESITI.find((e) => e[0] === k) || [k, k])[1];
const cadEsitoColor = (k) => k === "nessuna" ? "bg-emerald-100 text-emerald-700" : k === "contusione" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700";

export function CaduteOspite({ ospite }) {
  const qc = useQueryClient();
  const operatori = useOperatori(ospite.structure_id);
  const { opName } = useFirma();
  const [dlg, setDlg] = useState(null);
  const { data: all = [] } = useQuery({ queryKey: ["cadute", ospite.structure_id], queryFn: async () => (await api.get("/cadute", { params: { structure_id: ospite.structure_id } })).data });
  const list = all.filter((c) => c.ospite_id === ospite.id).sort((a, b) => `${b.data || ""}${b.ora || ""}`.localeCompare(`${a.data || ""}${a.ora || ""}`));
  const openNew = () => { const now = new Date(); setDlg({ data: now.toISOString().slice(0, 10), ora: now.toTimeString().slice(0, 5), esito: "nessuna", operatore: opName || "" }); };
  const save = async () => {
    const c = dlg;
    if (!c.operatore) { toast.error("Indica l'operatore in turno"); return; }
    if (!c.descrizione || !c.descrizione.trim()) { toast.error("Descrivi cosa è successo"); return; }
    const body = { ...c, ospite_id: ospite.id, ospite_nome: `${ospite.nome} ${ospite.cognome}`, structure_id: ospite.structure_id };
    try { if (c.id) await api.put(`/cadute/${c.id}`, body); else await api.post("/cadute", body); qc.invalidateQueries({ queryKey: ["cadute"] }); setDlg(null); toast.success("Caduta registrata"); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const del = async (id) => { try { await api.delete(`/cadute/${id}`); qc.invalidateQueries({ queryKey: ["cadute"] }); } catch { toast.error("Errore"); } };

  return (
    <div className="space-y-3" data-testid="scheda-cadute">
      <div className="flex items-center justify-between">
        <span className="font-head font-bold flex items-center gap-2"><AlertTriangle size={16} className="text-red-600" /> Cadute e infortuni</span>
        <Button size="sm" onClick={openNew} data-testid="scheda-caduta-new"><Plus size={14} className="mr-1" /> Nuova caduta</Button>
      </div>
      {list.length === 0 ? <p className="text-sm text-muted-foreground">Nessuna caduta registrata</p> : list.map((c) => (
        <div key={c.id} className="gr-card p-3 border-l-4 border-red-500" data-testid={`scheda-caduta-${c.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">{c.data ? fmtDate(c.data) : "—"} {c.ora || ""}</span>
                <span className={`font-bold px-2 py-0.5 rounded-full ${cadEsitoColor(c.esito)}`}>{cadEsitoLabel(c.esito)}</span>
                {c.operatore && <span>· {c.operatore}</span>}
                {c.luogo && <span>· {c.luogo}</span>}
              </div>
              {c.descrizione && <p className="text-sm mt-1 whitespace-pre-wrap">{c.descrizione}</p>}
            </div>
            <div className="flex gap-1 shrink-0">
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setDlg({ ...c })}><ChevronRight className="rotate-90" size={14} /></Button>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => del(c.id)} data-testid={`scheda-caduta-del-${c.id}`}><Trash2 size={14} /></Button>
            </div>
          </div>
        </div>
      ))}

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{dlg?.id ? "Modifica caduta" : "Nuova caduta"}</DialogTitle><DialogDescription>{ospite.cognome} {ospite.nome} — descrivi cosa è successo.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold mb-1 block">Operatore in turno *</Label>
              {opName ? <OperatoreLocked name={opName} testid="scheda-caduta-op" /> : (
              <Select value={dlg?.operatore || ""} onValueChange={(v) => setDlg((p) => ({ ...p, operatore: v }))}>
                <SelectTrigger data-testid="scheda-caduta-op"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{operatori.map((o) => <SelectItem key={o.id} value={`${o.nome} ${o.cognome}`}>{o.nome} {o.cognome}</SelectItem>)}</SelectContent>
              </Select>)}
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">Esito</Label>
              <Select value={dlg?.esito || "nessuna"} onValueChange={(v) => setDlg((p) => ({ ...p, esito: v }))}>
                <SelectTrigger data-testid="scheda-caduta-esito"><SelectValue /></SelectTrigger>
                <SelectContent>{CAD_ESITI.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs font-semibold mb-1 block">Data</Label><Input type="date" value={dlg?.data || ""} onChange={(e) => setDlg((p) => ({ ...p, data: e.target.value }))} /></div>
            <div><Label className="text-xs font-semibold mb-1 block">Ora</Label><Input type="time" value={dlg?.ora || ""} onChange={(e) => setDlg((p) => ({ ...p, ora: e.target.value }))} /></div>
            <div className="col-span-2"><Label className="text-xs font-semibold mb-1 block">Luogo</Label><Input value={dlg?.luogo || ""} onChange={(e) => setDlg((p) => ({ ...p, luogo: e.target.value }))} placeholder="es. bagno, camera..." /></div>
            <div className="col-span-2"><Label className="text-xs font-semibold mb-1 block">Descrizione — cosa è successo *</Label><Textarea rows={4} value={dlg?.descrizione || ""} onChange={(e) => setDlg((p) => ({ ...p, descrizione: e.target.value }))} data-testid="scheda-caduta-descrizione" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDlg(null)}>Annulla</Button><Button onClick={save} data-testid="scheda-caduta-save">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ============================ CONTROLLO TERAPIE ============================
export function ControlloTerapieOspite({ ospite }) {
  const [mese, shiftMese, setMese] = useMese();
  const { data: recs = [] } = useQuery({
    queryKey: ["ct-ospite", ospite.id],
    queryFn: async () => (await api.get("/controllo-terapie/storico", { params: { ospite_id: ospite.id } })).data,
  });
  const shown = recs.filter((r) => (r.data || "").startsWith(mese));
  return (
    <div className="gr-card p-4" data-testid="scheda-controllo-terapie">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <span className="font-head font-bold">Terapie non assunte</span>
        <div className="flex items-center gap-2">
          <MeseNav mese={mese} shiftMese={shiftMese} setMese={setMese} testid="scheda-ct-mese" />
          <span className="text-sm text-muted-foreground tabnum" data-testid="ct-osp-count">{shown.length}</span>
        </div>
      </div>
      {shown.length === 0 ? (
        <p className="text-sm text-muted-foreground py-3" data-testid="ct-osp-empty">Nessuna terapia non assunta in {meseLabel(mese)}.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-secondary/60 text-left">
              <th className="p-2 font-bold">Data</th>
              <th className="p-2 font-bold">Medicine non prese</th>
              <th className="p-2 font-bold">Nota</th>
              <th className="p-2 font-bold">Registrato da</th>
            </tr></thead>
            <tbody>
              {shown.map((r, i) => (
                <tr key={i} className="border-t" data-testid={`ct-osp-row-${i}`}>
                  <td className="p-2 tabnum whitespace-nowrap">{fmtDate(r.data)}</td>
                  <td className="p-2">{(r.farmaci_rifiutati || []).length ? <div className="flex flex-wrap gap-1">{r.farmaci_rifiutati.map((f, j) => <span key={j} className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">{f}</span>)}</div> : <span className="text-xs text-muted-foreground">Tutte / non specificato</span>}</td>
                  <td className="p-2 text-muted-foreground">{r.note || "—"}</td>
                  <td className="p-2 text-xs text-muted-foreground">{r.updated_by || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================ NON MANGIA / NON BEVE ============================
export function AlimentazioneOspite({ ospite, tipo, title, momentiLabel }) {
  const [mese, shiftMese, setMese] = useMese();
  const { data: recs = [] } = useQuery({
    queryKey: ["alim-ospite", tipo, ospite.id],
    queryFn: async () => (await api.get("/alimentazione/storico", { params: { tipo, ospite_id: ospite.id } })).data,
  });
  const shown = recs.filter((r) => (r.data || "").startsWith(mese));
  return (
    <div className="gr-card p-4" data-testid={`scheda-non-${tipo}`}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <span className="font-head font-bold">{title}</span>
        <div className="flex items-center gap-2">
          <MeseNav mese={mese} shiftMese={shiftMese} setMese={setMese} testid={`scheda-alim-mese-${tipo}`} />
          <span className="text-sm text-muted-foreground tabnum" data-testid={`alim-osp-count-${tipo}`}>{shown.length}</span>
        </div>
      </div>
      {shown.length === 0 ? (
        <p className="text-sm text-muted-foreground py-3" data-testid={`alim-osp-empty-${tipo}`}>Nessuna segnalazione in {meseLabel(mese)}.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-secondary/60 text-left">
              <th className="p-2 font-bold">Data</th>
              <th className="p-2 font-bold">{momentiLabel}</th>
              <th className="p-2 font-bold">Nota</th>
              <th className="p-2 font-bold">Registrato da</th>
            </tr></thead>
            <tbody>
              {shown.map((r, i) => (
                <tr key={i} className="border-t" data-testid={`alim-osp-${tipo}-row-${i}`}>
                  <td className="p-2 tabnum whitespace-nowrap">{fmtDate(r.data)}</td>
                  <td className="p-2">{(r.momenti || []).length ? <div className="flex flex-wrap gap-1">{r.momenti.map((f, j) => <span key={j} className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">{f}</span>)}</div> : <span className="text-xs text-muted-foreground">Non specificato</span>}</td>
                  <td className="p-2 text-muted-foreground">{r.note || "—"}</td>
                  <td className="p-2 text-xs text-muted-foreground">{r.updated_by || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ============================ DIETA E ALIMENTAZIONE ============================
const DIETA_PRESET = ["Diabetica", "Iposodica / senza sale", "Frullata / morbida (disfagia)", "Senza glutine", "Vegetariana", "Ipocalorica"];
export function DietaOspite({ ospite, onSaved }) {
  const qc = useQueryClient();
  const [allergie, setAllergie] = useState(Array.isArray(ospite.allergie) ? ospite.allergie.join(", ") : String(ospite.allergie || ""));
  const [dieta, setDieta] = useState(ospite.dieta || []);
  const [note, setNote] = useState(ospite.dieta_note || "");
  const [saving, setSaving] = useState(false);
  const toggleDieta = (d) => setDieta((p) => (p.includes(d) ? p.filter((x) => x !== d) : [...p, d]));
  const save = async () => {
    setSaving(true);
    const allergieArr = allergie.split(",").map((s) => s.trim()).filter(Boolean);
    try {
      await api.put(`/ospiti/${ospite.id}`, { allergie: allergieArr, dieta, dieta_note: note.trim() });
      toast.success("Dieta e alimentazione salvate");
      qc.invalidateQueries({ queryKey: ["scheda", ospite.id] });
      qc.invalidateQueries({ queryKey: ["menu-day"] });
      onSaved && onSaved();
    } catch { toast.error("Errore nel salvataggio"); }
    finally { setSaving(false); }
  };
  return (
    <div className="mt-4 pt-3 border-t" data-testid="scheda-dieta">
      <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5"><AlertTriangle size={13} /> Dieta e alimentazione</div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs font-semibold mb-1 block">Allergie / Intolleranze <span className="text-muted-foreground font-normal">(separate da virgola)</span></Label>
          <Input value={allergie} onChange={(e) => setAllergie(e.target.value)} placeholder="Es. lattosio, glutine, frutta secca" data-testid="dieta-allergie" />
        </div>
        <div>
          <Label className="text-xs font-semibold mb-1 block">Dieta personalizzata</Label>
          <div className="flex flex-wrap gap-1.5" data-testid="dieta-preset">
            {DIETA_PRESET.map((d) => {
              const sel = dieta.includes(d);
              return (
                <button key={d} type="button" onClick={() => toggleDieta(d)} data-testid={`dieta-preset-${d}`}
                  className={`min-h-[36px] px-2.5 py-1.5 rounded-lg border text-xs font-semibold transition-colors ${sel ? "bg-violet-600 text-white border-violet-600" : "bg-white hover:bg-violet-50 border-violet-200 text-violet-900"}`}>
                  {d}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <div className="mt-3">
        <Label className="text-xs font-semibold mb-1 block">Note dieta <span className="text-muted-foreground font-normal">(facoltative)</span></Label>
        <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Indicazioni particolari per la cucina…" data-testid="dieta-note" />
      </div>
      <div className="mt-2 flex justify-end">
        <Button size="sm" onClick={save} disabled={saving} data-testid="dieta-salva"><Check size={14} className="mr-1.5" /> {saving ? "Salvo…" : "Salva dieta"}</Button>
      </div>
    </div>
  );
}
